"use client";

import { c as _c } from "react/compiler-runtime";
import type { InitialConfigType } from "@lexical/react/LexicalComposer";
import type { FocusEvent } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { forwardRef, useImperativeHandle, useCallback, useEffect, useRef } from "react";
import type { EditorState } from "lexical";
import { RootNode, COMMAND_PRIORITY_CRITICAL, createCommand } from "lexical";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { ListNode, ListItemNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import type { MentionedUser, MentionedWave, ReferencedNft } from "@/entities/IDrop";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { MentionNode } from "../drops/create/lexical/nodes/MentionNode";
import { HashtagNode } from "../drops/create/lexical/nodes/HashtagNode";
import { WaveMentionNode } from "../drops/create/lexical/nodes/WaveMentionNode";
import { ImageNode } from "../drops/create/lexical/nodes/ImageNode";
import ExampleTheme from "../drops/create/lexical/ExampleTheme";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
import type { ClearEditorPluginHandles } from "../drops/create/lexical/plugins/ClearEditorPlugin";
import ClearEditorPlugin from "../drops/create/lexical/plugins/ClearEditorPlugin";
import type { NewMentionsPluginHandles } from "../drops/create/lexical/plugins/mentions/MentionsPlugin";
import NewMentionsPlugin from "../drops/create/lexical/plugins/mentions/MentionsPlugin";
import type { NewHastagsPluginHandles } from "../drops/create/lexical/plugins/hashtags/HashtagsPlugin";
import NewHashtagsPlugin from "../drops/create/lexical/plugins/hashtags/HashtagsPlugin";
import type { NewWaveMentionsPluginHandles } from "../drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import NewWaveMentionsPlugin from "../drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import { MaxLengthPlugin } from "../drops/create/lexical/plugins/MaxLengthPlugin";
import DragDropPastePlugin from "../drops/create/lexical/plugins/DragDropPastePlugin";
import EnterKeyPlugin from "../drops/create/lexical/plugins/enter/EnterKeyPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import CreateDropEmojiPicker from "./CreateDropEmojiPicker";
import useCapacitor from "@/hooks/useCapacitor";
import EmojiPlugin from "../drops/create/lexical/plugins/emoji/EmojiPlugin";
import { EmojiNode } from "../drops/create/lexical/nodes/EmojiNode";
import { SAFE_MARKDOWN_TRANSFORMERS } from "@/components/drops/create/lexical/transformers/markdownTransformers";
import PlainTextPastePlugin from "@/components/drops/create/lexical/plugins/PlainTextPastePlugin";
export interface CreateDropInputHandles {
  clearEditorState: () => void;
  focus: () => void;
}

// Create a custom command
const DISABLE_EDIT_COMMAND = createCommand("DISABLE_EDIT");

// Create a custom plugin to handle disabling
function DisableEditPlugin(t0) {
  const $ = _c(4);
  const {
    disabled
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== disabled || $[1] !== editor) {
    t1 = () => {
      if (disabled) {
        return editor.registerCommand(DISABLE_EDIT_COMMAND, _temp, COMMAND_PRIORITY_CRITICAL);
      }
      return _temp2;
    };
    t2 = [editor, disabled];
    $[0] = disabled;
    $[1] = editor;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp2() {}
function _temp() {
  return true;
}
const CreateDropInput = forwardRef<CreateDropInputHandles, {
  readonly waveId: string;
  readonly editorState: EditorState | null;
  readonly type: ActiveDropAction | null;
  readonly canSubmit: boolean;
  readonly isStormMode: boolean;
  readonly submitting: boolean;
  readonly isDropMode: boolean;
  readonly onDrop?: (() => void) | undefined;
  readonly onEditorState: (editorState: EditorState) => void;
  readonly onEditorBlur?: (event: FocusEvent<HTMLDivElement>) => void;
  readonly onReferencedNft: (referencedNft: ReferencedNft) => void;
  readonly onMentionedUser: (mentionedUser: Omit<MentionedUser, "current_handle">) => void;
  readonly onMentionedWave: (mentionedWave: MentionedWave) => void;
}>((t0, ref) => {
  const $ = _c(78);
  const {
    waveId,
    editorState,
    type,
    canSubmit,
    isStormMode,
    isDropMode,
    submitting,
    onEditorState,
    onEditorBlur,
    onReferencedNft,
    onMentionedUser,
    onMentionedWave,
    onDrop
  } = t0;
  const {
    isCapacitor
  } = useCapacitor();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [MentionNode, HashtagNode, WaveMentionNode, RootNode, HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, TableNode, TableCellNode, TableRowNode, AutoLinkNode, LinkNode, HorizontalRuleNode, ImageNode, EmojiNode];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = !submitting;
  let t3;
  if ($[1] !== editorState || $[2] !== t2) {
    t3 = {
      namespace: "User Drop",
      nodes: t1,
      editorState,
      editable: t2,
      onError(error) {
        throw error;
      },
      theme: ExampleTheme
    };
    $[1] = editorState;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const editorConfig = t3;
  let t4;
  if ($[4] !== onEditorState) {
    t4 = editorState_0 => onEditorState(editorState_0);
    $[4] = onEditorState;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const onEditorStateChange = t4;
  let t5;
  if ($[6] !== onMentionedUser) {
    t5 = user => {
      onMentionedUser(user);
    };
    $[6] = onMentionedUser;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const onMentionedUserAdded = t5;
  let t6;
  if ($[8] !== onMentionedWave) {
    t6 = wave => {
      onMentionedWave(wave);
    };
    $[8] = onMentionedWave;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const onMentionedWaveAdded = t6;
  let t7;
  if ($[10] !== onReferencedNft) {
    t7 = hashtag => onReferencedNft(hashtag);
    $[10] = onReferencedNft;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const onHashtagAdded = t7;
  let t8;
  if ($[12] !== isDropMode || $[13] !== isStormMode || $[14] !== type) {
    t8 = () => {
      if (isStormMode) {
        return "Add to the storm";
      }
      if (type === null) {
        return isDropMode ? "Create a drop" : "Create a post";
      }
      switch (type) {
        case ActiveDropAction.REPLY:
          {
            return isDropMode ? "Drop a reply" : "Post a reply";
          }
        case ActiveDropAction.QUOTE:
          {
            return isDropMode ? "Quote a drop" : "Post a quote";
          }
        default:
          {
            assertUnreachable(type);
            return "";
          }
      }
    };
    $[12] = isDropMode;
    $[13] = isStormMode;
    $[14] = type;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const getPlaceHolderText = t8;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    const urlRegExp = new RegExp(/((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/);
    t9 = function validateUrl(url) {
      return url === "https://" || urlRegExp.test(url);
    };
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const validateUrl = t9;
  const clearEditorRef = useRef(null);
  const editorRef = useRef(null);
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      clearEditorRef.current?.clearEditorState();
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const clearEditorState = t10;
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => ({
      clearEditorState,
      focus: () => {
        (editorRef.current?.querySelector("[contenteditable=\"true\"]") as HTMLElement).focus();
      }
    });
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  useImperativeHandle(ref, t11);
  const mentionsPluginRef = useRef(null);
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => !!mentionsPluginRef.current?.isMentionsOpen();
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const isMentionsOpen = t12;
  const hashtagPluginRef = useRef(null);
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => !!hashtagPluginRef.current?.isHashtagsOpen();
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const isHashtagsOpen = t13;
  const waveMentionsPluginRef = useRef(null);
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    const isWaveMentionsOpen = () => !!waveMentionsPluginRef.current?.isWaveMentionsOpen();
    t14 = () => !isMentionsOpen() && !isHashtagsOpen() && !isWaveMentionsOpen();
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const canSubmitWithEnter = t14;
  const canSubmitRef = useRef(canSubmit);
  const onDropRef = useRef(onDrop);
  let t15;
  let t16;
  if ($[22] !== canSubmit) {
    t15 = () => {
      canSubmitRef.current = canSubmit;
    };
    t16 = [canSubmit];
    $[22] = canSubmit;
    $[23] = t15;
    $[24] = t16;
  } else {
    t15 = $[23];
    t16 = $[24];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[25] !== onDrop) {
    t17 = () => {
      onDropRef.current = onDrop;
    };
    t18 = [onDrop];
    $[25] = onDrop;
    $[26] = t17;
    $[27] = t18;
  } else {
    t17 = $[26];
    t18 = $[27];
  }
  useEffect(t17, t18);
  let t19;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      if (!canSubmitRef.current || !onDropRef.current) {
        return;
      }
      onDropRef.current();
    };
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const handleSubmit = t19;
  let t20;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      touchAction: "manipulation"
    };
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== submitting) {
    t21 = e => {
      const target = e.currentTarget;
      if (!submitting) {
        Promise.resolve().then(() => {
          target.focus();
          const selection = window.getSelection();
          if (selection?.rangeCount === 0) {
            const range = document.createRange();
            range.selectNodeContents(target);
            range.collapse(false);
            selection.addRange(range);
          }
        });
      }
    };
    $[30] = submitting;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  const t22 = `editor-input-one-liner tw-form-input tw-block tw-max-h-[40vh] tw-w-full tw-resize-none tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-py-2.5 tw-pl-3 tw-text-base tw-font-normal tw-leading-6 tw-text-white tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out tw-scrollbar-thin tw-scrollbar-track-iron-900 tw-scrollbar-thumb-iron-600 placeholder:tw-text-iron-500 focus:tw-bg-iron-950 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 sm:tw-text-sm ${submitting ? "tw-cursor-default tw-opacity-50" : ""} ${isCapacitor ? "tw-pr-[35px]" : "tw-pr-[40px]"}`;
  let t23;
  if ($[32] !== onEditorBlur || $[33] !== t21 || $[34] !== t22) {
    t23 = <ContentEditable spellCheck={true} autoCorrect="on" style={t20} onClick={t21} onBlur={onEditorBlur} className={t22} />;
    $[32] = onEditorBlur;
    $[33] = t21;
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <CreateDropEmojiPicker />;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] !== t23) {
    t25 = <div className="tw-relative">{t23}{t24}</div>;
    $[37] = t23;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  const t26 = `editor-placeholder ${submitting ? "tw-opacity-50" : ""}`;
  const t27 = getPlaceHolderText();
  let t28;
  if ($[39] !== t26 || $[40] !== t27) {
    t28 = <span className={t26}>{t27}</span>;
    $[39] = t26;
    $[40] = t27;
    $[41] = t28;
  } else {
    t28 = $[41];
  }
  let t29;
  if ($[42] !== t25 || $[43] !== t28) {
    t29 = <RichTextPlugin contentEditable={t25} placeholder={t28} ErrorBoundary={LexicalErrorBoundary} />;
    $[42] = t25;
    $[43] = t28;
    $[44] = t29;
  } else {
    t29 = $[44];
  }
  let t30;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <HistoryPlugin />;
    $[45] = t30;
  } else {
    t30 = $[45];
  }
  let t31;
  if ($[46] !== onEditorStateChange) {
    t31 = <OnChangePlugin onChange={onEditorStateChange} />;
    $[46] = onEditorStateChange;
    $[47] = t31;
  } else {
    t31 = $[47];
  }
  let t32;
  if ($[48] !== onMentionedUserAdded || $[49] !== waveId) {
    t32 = <NewMentionsPlugin waveId={waveId} onSelect={onMentionedUserAdded} ref={mentionsPluginRef} />;
    $[48] = onMentionedUserAdded;
    $[49] = waveId;
    $[50] = t32;
  } else {
    t32 = $[50];
  }
  let t33;
  if ($[51] !== onMentionedWaveAdded) {
    t33 = <NewWaveMentionsPlugin onSelect={onMentionedWaveAdded} ref={waveMentionsPluginRef} />;
    $[51] = onMentionedWaveAdded;
    $[52] = t33;
  } else {
    t33 = $[52];
  }
  let t34;
  if ($[53] !== onHashtagAdded) {
    t34 = <NewHashtagsPlugin onSelect={onHashtagAdded} ref={hashtagPluginRef} />;
    $[53] = onHashtagAdded;
    $[54] = t34;
  } else {
    t34 = $[54];
  }
  let t35;
  let t36;
  let t37;
  let t38;
  let t39;
  let t40;
  let t41;
  let t42;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <MaxLengthPlugin maxLength={25000} />;
    t36 = <DragDropPastePlugin />;
    t37 = <ListPlugin />;
    t38 = <PlainTextPastePlugin />;
    t39 = <MarkdownShortcutPlugin transformers={SAFE_MARKDOWN_TRANSFORMERS} />;
    t40 = <TabIndentationPlugin />;
    t41 = <LinkPlugin validateUrl={validateUrl} />;
    t42 = <ClearEditorPlugin ref={clearEditorRef} />;
    $[55] = t35;
    $[56] = t36;
    $[57] = t37;
    $[58] = t38;
    $[59] = t39;
    $[60] = t40;
    $[61] = t41;
    $[62] = t42;
  } else {
    t35 = $[55];
    t36 = $[56];
    t37 = $[57];
    t38 = $[58];
    t39 = $[59];
    t40 = $[60];
    t41 = $[61];
    t42 = $[62];
  }
  let t43;
  let t44;
  if ($[63] !== submitting) {
    t43 = <DisableEditPlugin disabled={submitting} />;
    t44 = <EnterKeyPlugin handleSubmit={handleSubmit} canSubmitWithEnter={canSubmitWithEnter} disabled={submitting} />;
    $[63] = submitting;
    $[64] = t43;
    $[65] = t44;
  } else {
    t43 = $[64];
    t44 = $[65];
  }
  let t45;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <EmojiPlugin />;
    $[66] = t45;
  } else {
    t45 = $[66];
  }
  let t46;
  if ($[67] !== t29 || $[68] !== t31 || $[69] !== t32 || $[70] !== t33 || $[71] !== t34 || $[72] !== t43 || $[73] !== t44) {
    t46 = <div className="tw-flex tw-items-end tw-gap-x-3"><div className="tw-relative tw-w-full">{t29}{t30}{t31}{t32}{t33}{t34}{t35}{t36}{t37}{t38}{t39}{t40}{t41}{t42}{t43}{t44}{t45}</div></div>;
    $[67] = t29;
    $[68] = t31;
    $[69] = t32;
    $[70] = t33;
    $[71] = t34;
    $[72] = t43;
    $[73] = t44;
    $[74] = t46;
  } else {
    t46 = $[74];
  }
  let t47;
  if ($[75] !== editorConfig || $[76] !== t46) {
    t47 = <div className="tailwind-scope" ref={editorRef}><LexicalComposer initialConfig={editorConfig}>{t46}</LexicalComposer></div>;
    $[75] = editorConfig;
    $[76] = t46;
    $[77] = t47;
  } else {
    t47 = $[77];
  }
  return t47;
});
CreateDropInput.displayName = "CreateDropInput";
export default CreateDropInput;